import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

function setupNumberFormatters(locale: string, currency: string) {
  try {
    return {
      currency: new Intl.NumberFormat(locale, {
        style: 'currency',
        trailingZeroDisplay: 'auto',
        minimumFractionDigits: 0,
        currency,
      }),
      deltaCurrency: new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        trailingZeroDisplay: 'auto',
        minimumFractionDigits: 0,
        signDisplay: 'exceptZero',
      }),
    }
  } catch (e) {
    console.error(`Failed to create number formatters for locale ${locale} and currency ${currency}`, e)
    return undefined
  }
}

const formatterCache: Record<string, ReturnType<typeof setupNumberFormatters>> = {
  'en-US-USD': setupNumberFormatters('en-US', 'USD'),
  'de-DE-EUR': setupNumberFormatters('de-DE', 'EUR'),
}

// biome-ignore lint/style/noNonNullAssertion: is guaranteed to be present
const defaultFormatters = formatterCache['en-US-USD']!

function formatCurrency(formatter: Intl.NumberFormat | undefined, value: number, options?: { hideCurrencySymbol?: boolean }): string {
  const usedFormatter = formatter ?? defaultFormatters.currency

  if (options?.hideCurrencySymbol) {
    const parts = usedFormatter.formatToParts(value)
    return parts
      .filter((part) => part.type !== 'currency')
      .map((part) => part.value)
      .join('')
      .trim()
  }
  return usedFormatter.format(value)
}

function formatDeltaCurrency(formatter: Intl.NumberFormat | undefined, value: number, options?: { hideCurrencySymbol?: boolean }): string {
  const usedFormatter = formatter ?? defaultFormatters.deltaCurrency
  if (options?.hideCurrencySymbol) {
    const parts = usedFormatter.formatToParts(value)
    return parts
      .filter((part) => part.type !== 'currency')
      .map((part) => part.value)
      .join('')
      .trim()
  }
  return usedFormatter.format(value)
}

export const mapLangToLocale: Record<string, string> = {
  en: 'en-US',
  de: 'de-DE',
  fr: 'fr-FR',
  it: 'it-IT',
  dev: 'de-DE',
  pseudo: 'en-US',
  keys: 'en-US',
}

// TODO do we have some way to access the currency from the configuration
export const useCurrencyFormatter = (currency = 'EUR', locale?: string) => {
  const { i18n } = useTranslation('shared')
  const usedLocale = locale || mapLangToLocale[i18n.language] || 'en-US'
  const combinedKey = `${usedLocale}-${currency}`

  const formatter = formatterCache[combinedKey] ?? setupNumberFormatters(usedLocale, currency)
  formatterCache[combinedKey] = formatter
  return useMemo(() => {
    return {
      formatCurrency: (value: number, options?: { hideCurrencySymbol?: boolean }) => formatCurrency(formatter?.currency, value, options),
      formatDeltaCurrency: (value: number, options?: { hideCurrencySymbol?: boolean }) =>
        formatDeltaCurrency(formatter?.deltaCurrency, value, options),
    }
  }, [formatter])
}
